/**
 * Competitors an ad can pin the hero heading to with `?competitor=<slug>`.
 *
 * Competitor-specific ad groups point at /dimension-vs-competition?competitor=clean-harbors
 * so the visitor sees "Leaving Clean Harbors?" instead of the rotation. Only names in
 * this list can be shown: the param is looked up, never rendered, so a hand-edited URL
 * cannot put arbitrary text in the H1. Anything unrecognised falls back to the rotation.
 *
 * Matching ignores case and punctuation, so `safety-kleen`, `Safety_Kleen` and
 * `safetykleen` all resolve. `aliases` cover the other ways an ad URL might spell it.
 */
export type PinnableCompetitor = {
  slug: string
  /** What the heading shows. Must fit on one line — see `.h1` in vs-competition.module.css. */
  name: string
  aliases?: string[]
}

export const PINNABLE_COMPETITORS: PinnableCompetitor[] = [
  { slug: 'safety-kleen', name: 'Safety‑Kleen' },
  { slug: 'clean-harbors', name: 'Clean Harbors', aliases: ['kleen-harbor', 'clean-harbor'] },
  { slug: 'veolia', name: 'Veolia' },
  { slug: 'actenviro', name: 'ACTenviro' },
  { slug: 'crystal-clean', name: 'Crystal Clean' },
  { slug: 'clean-management', name: 'Clean Management' },
  { slug: 'waste-management', name: 'Waste Management', aliases: ['wm'] },
  { slug: 'republic-services', name: 'Republic Services', aliases: ['republic'] },
]

/** The URL param an ad sets. Also forwarded as attribution — see ATTRIBUTION_PARAMS. */
export const COMPETITOR_PARAM = 'competitor'

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '')

const BY_KEY = new Map<string, PinnableCompetitor>()
PINNABLE_COMPETITORS.forEach((competitor) => {
  ;[competitor.slug, ...(competitor.aliases ?? [])].forEach((key) => {
    BY_KEY.set(normalize(key), competitor)
  })
})

/** The competitor a query value names, or null when it names none we know. */
export const resolveCompetitor = (
  value: string | string[] | undefined,
): PinnableCompetitor | null => {
  const raw = Array.isArray(value) ? value[0] : value
  if (!raw) return null
  return BY_KEY.get(normalize(raw)) ?? null
}
